import { createSelector } from "reselect";

import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import { selectIsContactsVisible } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
// fallow-ignore-next-line boundary-violation -- shared sales helper lives in workspace zone; move pending
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";

import { excludeDeniedOwnerFields } from "../../../utils/crmColumns";
import { countMissingRequiredColumns, makeSelectRequiredColumns, makeSelectMappedFields } from "../../../utils/export";
import { getChangedTabNames, getTabsWithEmptyFields } from "../../../utils/utils";
import type { ExportName, ExportSetting } from "../../type";
import { selectCurrentExportTab } from "../common";
import { selectDynamicsLoadingState } from "../dynamicsSelectors";
import {
    getAccountsSimilarwebFieldOptions,
    getCompanyAsLeadsSimilarwebFieldOptions,
    getDynamicsContactsSimilarwebFieldOptions,
    getLeadsSimilarwebFieldOptions,
} from "../exportSimilarwebFieldOptions";

const management = createStatePropertySelector(selectSalesIntelligenceState("management"));
const dynamicsExport = createStatePropertySelector(management("dynamicsExport"));

export const selectDynamicsSettings = dynamicsExport("settings");
export const selectDynamicsIsLoadingSettings = dynamicsExport("isLoadingSettings");
const selectDynamicsPrevSettings = dynamicsExport("prevSettings");
export const selectDynamicsDeletableField = dynamicsExport("deletableField");
export const selectExportingToDynamics = dynamicsExport("exportingDomainsTo");
export const selectDynamicsNotification = dynamicsExport("notification");
export const selectDynamicsIsSavingSettings = dynamicsExport("isSavingSettings");

export const selectDynamicsCurrentSettings = createSelector(
    selectCurrentExportTab,
    selectDynamicsSettings,
    (tabName, allSettings): ExportSetting[] => allSettings[tabName] || [],
);

const makeSelectDynamicsMappings = (recordName: ExportName) =>
    createSelector(selectDynamicsSettings, (settings) => settings[recordName]);

const selectDynamicsAccountsMappings = makeSelectDynamicsMappings("accounts");
const selectDynamicsContactsMappings = makeSelectDynamicsMappings("contacts");
const selectDynamicsLeadsMappings = makeSelectDynamicsMappings("leads");
const selectDynamicsCompanyAsLeadsMappings = makeSelectDynamicsMappings("companyAsLeads");

const selectDynamicsAccountsMappedFields = makeSelectMappedFields(selectDynamicsAccountsMappings);
const selectDynamicsContactsMappedFields = makeSelectMappedFields(selectDynamicsContactsMappings);
const selectDynamicsLeadsMappedFields = makeSelectMappedFields(selectDynamicsLeadsMappings);
const selectDynamicsCompanyAsLeadsMappedFields = makeSelectMappedFields(selectDynamicsCompanyAsLeadsMappings);

const selectDynamicsColumns = dynamicsExport("columns");

export const makeSelectDynamicsColumnsRaw = (recordName: ExportName) =>
    createSelector(selectDynamicsColumns, (columns) => columns[recordName]);

export const makeSelectDynamicsColumns = (recordName: ExportName) =>
    createSelector(makeSelectDynamicsColumnsRaw(recordName), excludeDeniedOwnerFields);

export const selectDynamicsAccountsColumns = makeSelectDynamicsColumns("accounts");
export const selectDynamicsContactsColumns = makeSelectDynamicsColumns("contacts");
export const selectDynamicsLeadsColumns = makeSelectDynamicsColumns("leads");
export const selectDynamicsCompanyAsLeadsColumns = makeSelectDynamicsColumns("companyAsLeads");

const selectDynamicsRequiredAccountsColumns = makeSelectRequiredColumns(selectDynamicsAccountsColumns);
const selectDynamicsRequiredContactsColumns = makeSelectRequiredColumns(selectDynamicsContactsColumns);
const selectDynamicsRequiredLeadsColumns = makeSelectRequiredColumns(selectDynamicsLeadsColumns);
const selectDynamicsRequiredCompanyAsLeadsColumns = makeSelectRequiredColumns(selectDynamicsCompanyAsLeadsColumns);

export const selectDynamicsAccountsMissingRequiredFieldsCount = createSelector(
    selectDynamicsRequiredAccountsColumns,
    selectDynamicsAccountsMappedFields,
    countMissingRequiredColumns,
);

export const selectDynamicsContactsMissingRequiredFieldsCount = createSelector(
    selectDynamicsRequiredContactsColumns,
    selectDynamicsContactsMappedFields,
    countMissingRequiredColumns,
);

export const selectDynamicsLeadsMissingRequiredFieldsCount = createSelector(
    selectDynamicsRequiredLeadsColumns,
    selectDynamicsLeadsMappedFields,
    countMissingRequiredColumns,
);

export const selectDynamicsCompanyAsLeadsMissingRequiredFieldsCount = createSelector(
    selectDynamicsRequiredCompanyAsLeadsColumns,
    selectDynamicsCompanyAsLeadsMappedFields,
    countMissingRequiredColumns,
);

export const selectDynamicsMissingRequiredFieldsCount = createSelector(
    selectDynamicsAccountsMissingRequiredFieldsCount,
    selectDynamicsContactsMissingRequiredFieldsCount,
    selectDynamicsLeadsMissingRequiredFieldsCount,
    selectDynamicsCompanyAsLeadsMissingRequiredFieldsCount,
    (accounts, contacts, leads, companyAsLeads) => ({ accounts, contacts, leads, companyAsLeads }),
);

export const selectDynamicsPrevSettingsByCurrentType = createSelector(
    selectCurrentExportTab,
    selectDynamicsPrevSettings,
    (tabName, allPrevSettings): ExportSetting[] => allPrevSettings[tabName] || [],
);

export const selectDynamicsTotalSettings = createSelector(
    selectDynamicsCurrentSettings,
    (settings): number => settings.length,
);

export const selectDynamicsTabsWithEmptyFields = createSelector(
    selectDynamicsSettings,
    selectIsContactsVisible,
    (allExportSettings, isContactsVisible): ExportName[] =>
        getTabsWithEmptyFields({ allExportSettings, isContactsVisible }),
);

export const selectDynamicsIsTabsWithEmptyFields = createSelector(
    selectDynamicsTabsWithEmptyFields,
    (tabsWithEmptyFields) => !!tabsWithEmptyFields.length,
);

export const selectDynamicsSettingsChangedTabNames = createSelector(
    selectDynamicsSettings,
    selectDynamicsPrevSettings,
    getChangedTabNames,
);

export const selectIsDynamicsSettingsChanged = createSelector(
    selectDynamicsSettingsChangedTabNames,
    (tabNames) => !!tabNames.length,
);

export const selectDynamicsNotSelectedSettings = getAccountsSimilarwebFieldOptions;

export const selectDynamicsContactsNotSelectedSettings = getDynamicsContactsSimilarwebFieldOptions;

export const selectDynamicsLeadsNotSelectedSettings = getLeadsSimilarwebFieldOptions;

export const selectDynamicsCompanyAsLeadsNotSelectedSettings = getCompanyAsLeadsSimilarwebFieldOptions;

export const selectDynamicsIsColumnsLoading = dynamicsExport("isColumnsLoading");

export const selectIsLoadingDynamicsExportPreferences = createSelector(
    selectDynamicsIsColumnsLoading,
    selectDynamicsIsLoadingSettings,
    selectDynamicsLoadingState,
    (isLoadingColumns, isLoadingSettings, isLoadingStatus) => {
        return isLoadingColumns || isLoadingSettings || isLoadingStatus;
    },
);
